"use client";

import MediaImage from "@/components/ui/MediaImage";

import { useMemo, useState, type CSSProperties } from "react";
import clsx from "clsx";
import type { CaseData } from "@/cms/models";
import CaseMediaViewer, {
  type CaseMediaViewerItem,
} from "@/components/ui/CaseMediaViewer";
import RippleCtaButton from "@/components/ui/RippleCtaButton";
import {
  CASE_IMAGE_GRID_ANCHOR,
  scrollToCaseImageGrid,
} from "@/utils/case-gallery";
import {
  CASE_IMAGE_GRID_RADIUS_PX,
  CASE_IMAGE_GRID_VARIANTS,
  caseImageGridCounts,
  caseImageGridCut,
  caseImageGridCutHeight,
  caseImageGridFadeHeight,
  caseImageGridSrcSet,
  gridLengthCss,
  layoutCaseImageGridVariants,
  type CaseImageGridFilter,
  type CaseImageGridLayout,
  type CaseImageGridMedia,
  type CaseImageGridMediaKind,
  type CaseImageGridSkip,
  type CaseImageGridTile,
  type CaseImageGridVariant,
  type CaseImageGridVariants,
  type ResolvedCaseImageGridSettings,
} from "@/utils/case-image-grid";
import CaseImageGridVideo from "./CaseImageGridVideo.client";

type PerVariant<T> = Record<CaseImageGridVariant, T>;

const STEP = 8;
const VARIANT_QUERY: PerVariant<string> = {
  m: "(max-width: 639.98px)",
  s: "(min-width: 640px) and (max-width: 767.98px)",
  t: "(min-width: 768px) and (max-width: 1023.98px)",
  d: "(min-width: 1024px)",
};
const TYPES: [CaseImageGridFilter, string][] = [
  ["all", "Alle"],
  ["photo", "Billeder"],
  ["video", "Video"],
  ["drawing", "Plantegninger"],
];

const perVariant = <T,>(map: (variant: CaseImageGridVariant) => T) =>
  Object.fromEntries(
    CASE_IMAGE_GRID_VARIANTS.map((variant) => [variant, map(variant)]),
  ) as PerVariant<T>;

const pictures = (count: number) =>
  `${count} ${count === 1 ? "billede" : "billeder"}`;

const offClasses = (shown: CaseImageGridVariant[]) =>
  CASE_IMAGE_GRID_VARIANTS.filter((variant) => !shown.includes(variant))
    .map((variant) => `re-image-grid-off-${variant}`)
    .join(" ");

/** Text that differs per breakpoint, switched by CSS so the server markup never changes. */
function ByVariant({ values }: { values: PerVariant<string> }) {
  const groups: { value: string; variants: CaseImageGridVariant[] }[] = [];
  for (const variant of CASE_IMAGE_GRID_VARIANTS) {
    const last = groups.at(-1);
    if (last?.value === values[variant]) last.variants.push(variant);
    else groups.push({ value: values[variant], variants: [variant] });
  }
  if (groups.length === 1) return <>{groups[0].value}</>;
  return (
    <>
      {groups.map((group) => (
        <span
          key={group.variants.join("")}
          className={offClasses(group.variants)}
        >
          {group.value}
        </span>
      ))}
    </>
  );
}

function activeVariant(): CaseImageGridVariant {
  return (
    CASE_IMAGE_GRID_VARIANTS.find(
      (variant) => window.matchMedia(VARIANT_QUERY[variant]).matches,
    ) ?? "d"
  );
}

function viewerItem(media: CaseImageGridMedia): CaseMediaViewerItem {
  return media.kind === "video"
    ? { type: "video", url: media.video ?? "" }
    : { type: "image", image: media.image! };
}

export interface CaseImageGridProps {
  caseData: Pick<
    CaseData,
    "advertisementAddressOrDisplayName" | "shortAddress"
  >;
  media: CaseImageGridMedia[];
  settings: ResolvedCaseImageGridSettings;
  skip: CaseImageGridSkip;
  /** The server's layouts for the unfiltered grid; the client only recomputes for a type filter. */
  variants: CaseImageGridVariants;
  /** The section's maximum content width, for `sizes`; absent for full-width sections. */
  maxWidth?: number;
  fadeColor: string;
  /** Over a background picture or layer the last visible row fades out itself instead of into `fadeColor`. */
  fadesOut?: boolean;
}

export default function CaseImageGrid({
  caseData,
  media,
  settings,
  skip,
  variants,
  maxWidth,
  fadeColor,
  fadesOut = false,
}: CaseImageGridProps) {
  const [filter, setFilter] = useState<CaseImageGridFilter>("all");
  const [expanded, setExpanded] = useState(false);
  const [steps, setSteps] = useState(0);
  const [viewer, setViewer] = useState<{
    media: CaseMediaViewerItem[];
    index: number;
  } | null>(null);
  const items = useMemo(
    () => media.map(({ kind, width, height }) => ({ kind, width, height })),
    [media],
  );
  const current = useMemo(
    () =>
      filter === "all"
        ? variants
        : layoutCaseImageGridVariants(items, settings, skip, filter),
    [filter, items, settings, skip, variants],
  );
  const hasSmallTablet = !!current.s;
  const layouts: PerVariant<CaseImageGridLayout> = {
    m: current.m,
    s: current.s ?? current.t,
    t: current.t,
    d: current.d,
  };
  const tiles = perVariant(
    (variant) =>
      new Map<number, CaseImageGridTile>(
        layouts[variant].tiles.map((tile) => [tile.item, tile]),
      ),
  );
  const initialCut = perVariant((variant) =>
    settings.initialCount === 0
      ? layouts[variant].blocks.length
      : caseImageGridCut(layouts[variant], settings.initialCount),
  );
  const cut = perVariant((variant) => {
    const layout = layouts[variant];
    let count = initialCut[variant];
    if (settings.showAll === "expand" && expanded) return layout.blocks.length;
    if (settings.showAll === "step")
      for (
        let step = 0;
        step < steps && count < layout.blocks.length;
        step += 1
      )
        count = caseImageGridCut(
          layout,
          layout.blocks[count - 1].end + STEP,
          count,
        );
    return count;
  });
  const shownTiles = perVariant((variant) =>
    cut[variant] ? layouts[variant].blocks[cut[variant] - 1].end : 0,
  );
  const collapsible = CASE_IMAGE_GRID_VARIANTS.filter(
    (variant) => initialCut[variant] < layouts[variant].blocks.length,
  );
  const clickable = settings.viewer !== "none";
  const order = useMemo(() => {
    const desktop = current.d.tiles.map((tile) => tile.item);
    const seen = new Set(desktop);
    const extra = [current.m, current.s, current.t]
      .flatMap((layout) => layout?.tiles ?? [])
      .map((tile) => tile.item)
      .filter((item) => !seen.has(item) && seen.add(item))
      .sort((a, b) => a - b);
    return [...extra, ...desktop];
  }, [current]);
  const counts = perVariant((variant) =>
    caseImageGridCounts(
      items,
      settings,
      variant === "m" || variant === "s" ? skip.mobile : skip.desktop,
    ),
  );
  const present = (
    ["photo", "video", "drawing"] as CaseImageGridMediaKind[]
  ).filter((kind) =>
    CASE_IMAGE_GRID_VARIANTS.some((variant) => counts[variant][kind] > 0),
  );

  const open = (item?: number) => {
    const variant = activeVariant();
    const list = layouts[variant].tiles.map((tile) => tile.item);
    if (!list.length) return;
    const index =
      item === undefined
        ? Math.min(shownTiles[variant], list.length - 1)
        : Math.max(0, list.indexOf(item));
    setViewer({ index, media: list.map((entry) => viewerItem(media[entry])) });
  };
  const more = () => {
    if (settings.showAll === "viewer") return open();
    if (settings.showAll === "expand") {
      if (expanded) scrollToCaseImageGrid();
      setExpanded(!expanded);
      return;
    }
    const variant = activeVariant();
    if (cut[variant] < layouts[variant].blocks.length) setSteps(steps + 1);
    else {
      setSteps(0);
      scrollToCaseImageGrid();
    }
  };

  const canvasStyle: Record<string, string> = {};
  for (const variant of CASE_IMAGE_GRID_VARIANTS) {
    if (variant === "s" && !hasSmallTablet) continue;
    const layout = layouts[variant];
    canvasStyle[`--g${variant}`] = gridLengthCss(
      caseImageGridCutHeight(layout, cut[variant]),
    );
    const faded = cut[variant] < layout.blocks.length;
    if (faded || variant === "s")
      canvasStyle[`--f${variant}v`] = faded ? "block" : "none";
    if (faded)
      canvasStyle[`--f${variant}h`] = `min(140px, ${gridLengthCss(
        caseImageGridFadeHeight(layout, cut[variant]),
      )})`;
    if (fadesOut)
      canvasStyle[`--f${variant}m`] = faded
        ? canvasStyle[`--f${variant}h`]
        : "0px";
  }

  const tileStyle = (item: number) => {
    const style: Record<string, string> = {};
    for (const variant of CASE_IMAGE_GRID_VARIANTS) {
      if (variant === "s" && !hasSmallTablet) continue;
      const tile = tiles[variant].get(item);
      if (!tile || tile.block >= cut[variant]) {
        style[`--${variant}v`] = "none";
        continue;
      }
      style[`--${variant}x`] = gridLengthCss(tile.x);
      style[`--${variant}y`] = gridLengthCss(tile.y);
      style[`--${variant}w`] = gridLengthCss(tile.width);
      style[`--${variant}h`] = gridLengthCss(tile.height);
      if (variant === "s") style["--sv"] = "block";
    }
    return style as CSSProperties;
  };
  const sizes = (item: number) => {
    const share = (variant: CaseImageGridVariant) =>
      tiles[variant].get(item)?.width.unit ??
      tiles.d.get(item)?.width.unit ??
      1;
    const vw = (variant: CaseImageGridVariant) =>
      `${Math.max(1, Math.round(share(variant) * 100))}vw`;
    return [
      maxWidth &&
        `(min-width: ${maxWidth}px) ${Math.round(share("d") * maxWidth)}px`,
      `(min-width: 1024px) ${vw("d")}`,
      `(min-width: 768px) ${vw("t")}`,
      `(min-width: 640px) ${vw("s")}`,
      vw("m"),
    ]
      .filter(Boolean)
      .join(", ");
  };

  const total = order.length;
  return (
    <div
      id={CASE_IMAGE_GRID_ANCHOR}
      className="re-image-grid"
      data-fade={fadesOut ? "out" : undefined}
      role="region"
      aria-label="Billeder af boligen"
      style={
        {
          "--re-grid-radius": `${CASE_IMAGE_GRID_RADIUS_PX[settings.radius]}px`,
          "--re-grid-fade": fadeColor,
        } as CSSProperties
      }
    >
      <div
        className="re-image-grid-canvas"
        style={canvasStyle as CSSProperties}
      >
        {order.map((item, position) => {
          const entry = media[item];
          const eager = CASE_IMAGE_GRID_VARIANTS.some(
            (variant) => tiles[variant].get(item)?.block === 0,
          );
          const fit = (
            tiles.d.get(item) ??
            tiles.m.get(item) ??
            tiles.t.get(item)
          )?.fit;
          const label =
            entry.kind === "video"
              ? `Video ${position + 1} af ${total}`
              : entry.image?.description ||
                `Billede ${position + 1} af ${total}`;
          const caption =
            entry.kind !== "video" ? entry.image?.description : "";
          const content = (
            <>
              <span className="re-image-grid-media">
                {entry.kind === "video" ? (
                  <>
                    <CaseImageGridVideo url={entry.video ?? ""} />
                    <span className="re-image-grid-chip" aria-hidden="true">
                      Video
                    </span>
                  </>
                ) : (
                  <MediaImage
                    src={
                      entry.image!.lgUrl ||
                      entry.image!.mdUrl ||
                      entry.image!.xlUrl
                    }
                    srcSet={caseImageGridSrcSet(entry.image!)}
                    sizes={
                      caseImageGridSrcSet(entry.image!)
                        ? sizes(item)
                        : undefined
                    }
                    alt={label}
                    loading={eager ? "eager" : "lazy"}
                    // Keeps React from hoisting a preload that would compete with the page's hero.
                    fetchPriority={eager ? "low" : undefined}
                    decoding="async"
                  />
                )}
                {caption && settings.captions === "hover" && (
                  <span className="re-image-grid-caption" aria-hidden="true">
                    {caption}
                  </span>
                )}
              </span>
              {caption && settings.captions === "below" && (
                <span
                  className="re-image-grid-caption-below"
                  aria-hidden="true"
                >
                  {caption}
                </span>
              )}
            </>
          );
          const shared = {
            className: "re-image-grid-tile",
            style: tileStyle(item),
            "data-kind": entry.kind,
            "data-fit": fit,
          };
          return clickable ? (
            <button
              key={item}
              type="button"
              aria-haspopup="dialog"
              aria-label={entry.kind === "video" ? label : undefined}
              onClick={() => open(item)}
              {...shared}
            >
              {content}
            </button>
          ) : (
            <div
              key={item}
              role={entry.kind === "video" ? "img" : undefined}
              aria-label={entry.kind === "video" ? label : undefined}
              {...shared}
            >
              {content}
            </div>
          );
        })}
        <div className="re-image-grid-fade" aria-hidden="true" />
      </div>
      {collapsible.length > 0 && (
        <div
          className={clsx("re-image-grid-more", offClasses(collapsible))}
          style={
            settings.showAll === "step"
              ? (Object.fromEntries(
                  CASE_IMAGE_GRID_VARIANTS.filter(
                    (variant) => variant !== "s" || hasSmallTablet,
                  ).map((variant) => [
                    `--p${variant}`,
                    `${Math.round((shownTiles[variant] / Math.max(1, layouts[variant].tiles.length)) * 100)}%`,
                  ]),
                ) as CSSProperties)
              : undefined
          }
        >
          {settings.showAll === "step" && (
            <>
              <span className="homepage-text-sm" aria-live="polite">
                Viser{" "}
                <ByVariant
                  values={perVariant(
                    (variant) =>
                      `${shownTiles[variant]} af ${layouts[variant].tiles.length}`,
                  )}
                />{" "}
                billeder
              </span>
              <span className="re-image-grid-progress" aria-hidden="true">
                <span />
              </span>
            </>
          )}
          <RippleCtaButton onClick={more}>
            <ByVariant
              values={perVariant((variant) => {
                const layout = layouts[variant];
                if (settings.showAll === "expand" && expanded)
                  return "Vis færre billeder";
                if (settings.showAll === "step") {
                  if (cut[variant] >= layout.blocks.length)
                    return "Vis færre billeder";
                  const next = caseImageGridCut(
                    layout,
                    shownTiles[variant] + STEP,
                    cut[variant],
                  );
                  return `Vis ${pictures(layout.blocks[next - 1].end - shownTiles[variant])} mere`;
                }
                return `Vis alle ${pictures(layout.tiles.length)}`;
              })}
            />
          </RippleCtaButton>
        </div>
      )}
      {settings.typeMenu && present.length > 1 && (
        <nav className="re-image-grid-types" aria-label="Vis billedtyper">
          <div>
            {TYPES.filter(
              ([value]) =>
                value === "all" ||
                present.includes(value as CaseImageGridMediaKind),
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => {
                  setFilter(value);
                  setSteps(0);
                }}
              >
                {label}
                <span className="re-image-grid-count">
                  <ByVariant
                    values={perVariant((variant) => {
                      const count = counts[variant];
                      return String(
                        value === "all"
                          ? count.photo + count.video + count.drawing
                          : count[value as CaseImageGridMediaKind],
                      );
                    })}
                  />
                </span>
              </button>
            ))}
          </div>
        </nav>
      )}
      {viewer && (
        <CaseMediaViewer
          caseData={{
            ...caseData,
            images: [],
            imageUrl: "",
            videoUrls: [],
          }}
          variant={settings.viewer === "slideshow" ? "slideshow" : "scroll"}
          view="photo"
          media={viewer.media}
          initialIndex={viewer.index}
          onClose={() => setViewer(null)}
        />
      )}
    </div>
  );
}
